import React from 'react';
import GeneralDialog from '../../internal-components/GeneralDialog';
import LinkIcon from '@material-ui/icons/Link';
import styled from 'styled-components/macro';
import Link from '../Link';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Divider, Tooltip } from '@material-ui/core';
import useTranslations from '../../hooks/useTranslations';
import { TextString } from '../Text';

const StyledLinkIcon = styled(LinkIcon)`
  color: ${({ theme }) => theme.palette.primary.main}!important;
  position: relative;
  top: -4px;
`;

const LinkedHeaderContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const FormHeader = styled.div`
  font-weight: 500;
`;

const FormTitle = styled.span`
  color: ${({ theme }) => theme.palette.primary.main}!important;
`;

const FieldTitle = styled.span`
  color: ${({ theme }) => theme.palette.primary.main}!important;
`;

const SectionTitle = styled.div`
  color: #1e202d;
  font-weight: 500;
  line-height: 22px;
  margin-top: 1rem;
  font-size: 16px;
`;

const DetailBody = styled.div`
  flex: 1;
  margin-top: -1rem;
`;

const DetailInnerContainer = styled.div`
  padding: 0 1rem;
`;

const ExpansionPanelContainer = styled.div`
  padding: 1px 0;
`;

const ExplanationDiv = styled.div`
  margin: 1rem 0;
`;

const StyledDivider = styled(Divider)`
  && {
  }
`;

const CantDeleteFieldDialogView = props => {
  const t = useTranslations();
  return (
    <GeneralDialog
      open={props.showCantDeleteFieldDialog}
      title={
        props.isSection
          ? 'Section cannot be deleted'
          : 'Field cannot be deleted'
      }
      handleClose={() => {
        props.setShowCantDeleteFieldDialog(false);
      }}
      handleConfirm={props.clickedDeleteAnyway}
      buttons={
        props.hasScoringDependencies
          ? []
          : [
              {
                text: 'dialog.cancel',
                variant: 'outlined'
              },
              {
                text: 'delete_section_dialog.confirm',
                color: 'secondary',
                onClick: 'confirm'
              }
            ]
      }
    >
      <ExplanationDiv>
        <TextString
          content={
            props.isSection
              ? props.hasScoringDependencies
                ? t('form-builder.section.cant-delete-dialog.explanation1')
                : t('form-builder.section.shouldnt-delete-dialog.explanation1')
              : t('form-builder.field.cant-delete-dialog.explanation1')
          }
        />
      </ExplanationDiv>
      <ExplanationDiv>
        <TextString
          content={
            props.isSection
              ? props.hasScoringDependencies
                ? t('form-builder.section.cant-delete-dialog.explanation2')
                : t('form-builder.section.shouldnt-delete-dialog.explanation2')
              : t('form-builder.field.cant-delete-dialog.explanation2')
          }
        />
      </ExplanationDiv>
      {!props.isSection ? (
        <ExplanationDiv>
          <TextString content="form-builder.field.cant-delete-dialog.explanation3" />
        </ExplanationDiv>
      ) : (
        ''
      )}
      <>
        {props.dependencyFieldIds.map(dependentFieldId => {
          let hasCurrentFormDependencies = false;
          const dependencyDataForCurrentField =
            props.dependencyData?.dependenciesByField[dependentFieldId];
          const currentFormScoringDependencies =
            dependencyDataForCurrentField?.scoringFieldDependencies || false;
          const currentFormVisibilityDependencies =
            dependencyDataForCurrentField?.visibilityDependencies || false;
          const currentFormAutomationDependencies =
            dependencyDataForCurrentField?.automationDependencies || false;
          const currentFormReminderDependencies =
            dependencyDataForCurrentField?.reminderDependencies || false;
          const currentFieldLookupDependencies =
            dependencyDataForCurrentField?.lookupDependencies || false;
          hasCurrentFormDependencies =
            currentFormScoringDependencies ||
            currentFormVisibilityDependencies ||
            currentFormAutomationDependencies ||
            currentFormReminderDependencies;
          let fieldTitle = 'Untitled field';
          const matchingField = props.allFields.find(
            one => one.internalId === dependentFieldId
          );
          if (matchingField && matchingField.title) {
            fieldTitle = matchingField.title;
          }
          return (
            <div key={dependentFieldId}>
              {hasCurrentFormDependencies ? (
                <ExpansionPanelContainer>
                  <ExpansionPanel
                    expanded={
                      props.expandedAccordion ===
                      'current-form-' + dependentFieldId
                    }
                    onChange={props.handleAccordionClick(
                      'current-form-' + dependentFieldId
                    )}
                  >
                    <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                      <FormHeader>
                        {props.isSection ? (
                          <div>
                            Form: <FormTitle>Current Form</FormTitle>
                            <br />
                            Field: <FieldTitle>{fieldTitle}</FieldTitle>
                          </div>
                        ) : (
                          <div>
                            Form: <FormTitle>Current Form</FormTitle>
                          </div>
                        )}
                      </FormHeader>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                      <DetailBody>
                        <StyledDivider />
                        <DetailInnerContainer>
                          {currentFormScoringDependencies ? (
                            <div>
                              <SectionTitle>
                                <strong>
                                  Scoring (must be removed to continue)
                                </strong>
                              </SectionTitle>
                              <ul>
                                {currentFormScoringDependencies.map(
                                  (one, k) => (
                                    <li key={'scoring-field-' + k}>
                                      {one || 'Untitled scoring field'}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ) : (
                            ''
                          )}
                          {currentFormVisibilityDependencies ? (
                            <div>
                              <SectionTitle>Visibility</SectionTitle>
                              <ul>
                                {currentFormVisibilityDependencies.map(
                                  (one, k) => (
                                    <li key={'visibility-rule-' + k}>
                                      {one || 'Untitled field'}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ) : (
                            ''
                          )}
                          {currentFormAutomationDependencies.length ? (
                            <div>
                              <SectionTitle>Automations</SectionTitle>
                              <ul>
                                {currentFormAutomationDependencies.map(
                                  (one, k) => (
                                    <li key={'automation-rule-' + k}>
                                      {one || 'Untitled field'}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ) : (
                            ''
                          )}
                          {currentFormReminderDependencies.length ? (
                            <div>
                              <SectionTitle>Reminders</SectionTitle>
                              <ul>
                                {currentFormReminderDependencies.map(
                                  (one, k) => (
                                    <li key={'reminders-rule-' + k}>
                                      {one || 'Untitled field'}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ) : (
                            ''
                          )}
                        </DetailInnerContainer>
                      </DetailBody>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                </ExpansionPanelContainer>
              ) : (
                ''
              )}
              {currentFieldLookupDependencies
                ? currentFieldLookupDependencies.map((one, k) => {
                    return (
                      <ExpansionPanelContainer key={'form-' + k}>
                        <ExpansionPanel
                          expanded={
                            props.expandedAccordion ===
                            one.formId + '-' + dependentFieldId + '-' + k
                          }
                          onChange={props.handleAccordionClick(
                            one.formId + '-' + dependentFieldId + '-' + k
                          )}
                        >
                          <ExpansionPanelSummary
                            expandIcon={<ExpandMoreIcon />}
                          >
                            <FormHeader>
                              {props.isSection ? (
                                <LinkedHeaderContainer>
                                  <div>
                                    <Tooltip title="Go to form">
                                      <Link
                                        to={`/form-builder/${one.formId}`}
                                        onClick={() => {
                                          setTimeout(() => {
                                            window.location.reload();
                                          }, 1);
                                        }}
                                      >
                                        <StyledLinkIcon />
                                      </Link>
                                    </Tooltip>
                                  </div>
                                  <div>
                                    <div>
                                      Form:{' '}
                                      <Tooltip title="Go to form">
                                        <Link
                                          to={`/form-builder/${one.formId}`}
                                          onClick={() => {
                                            setTimeout(() => {
                                              window.location.reload();
                                            }, 1);
                                          }}
                                        >
                                          <FormTitle>
                                            {one.formTitle || 'Current form'}
                                          </FormTitle>
                                        </Link>
                                      </Tooltip>
                                    </div>

                                    <div>
                                      Field:{' '}
                                      <Tooltip title="Go to form">
                                        <Link
                                          to={`/form-builder/${one.formId}`}
                                          onClick={() => {
                                            setTimeout(() => {
                                              window.location.reload();
                                            }, 1);
                                          }}
                                        >
                                          <FieldTitle>
                                            {fieldTitle || 'Untitled field'}
                                          </FieldTitle>
                                        </Link>
                                      </Tooltip>
                                    </div>
                                  </div>
                                </LinkedHeaderContainer>
                              ) : (
                                <LinkedHeaderContainer>
                                  <StyledLinkIcon />
                                  Form:{' '}
                                  <Tooltip title="Go to form">
                                    <Link
                                      to={`/form-builder/${one.formId}`}
                                      onClick={() => {
                                        setTimeout(() => {
                                          window.location.reload();
                                        }, 1);
                                      }}
                                    >
                                      <FormTitle>
                                        {one.formTitle || 'Current form'}
                                      </FormTitle>
                                    </Link>
                                  </Tooltip>
                                </LinkedHeaderContainer>
                              )}
                            </FormHeader>
                          </ExpansionPanelSummary>
                          <ExpansionPanelDetails>
                            <DetailBody>
                              <StyledDivider />
                              <DetailInnerContainer>
                                <ul>
                                  {one.fieldTitles.map((otherFieldTitle, j) => (
                                    <li key={'field-' + j}>
                                      {otherFieldTitle || 'Untitled field'}
                                    </li>
                                  ))}
                                </ul>
                              </DetailInnerContainer>
                            </DetailBody>
                          </ExpansionPanelDetails>
                        </ExpansionPanel>
                      </ExpansionPanelContainer>
                    );
                  })
                : ''}
            </div>
          );
        })}
      </>
    </GeneralDialog>
  );
};

export default CantDeleteFieldDialogView;
